import {BrowserRuntime} from './browser';
import type {CreatorBrowserBinding} from './creator-browser';
import type { FrameTime, MediaState, SourceContext, SourceRuntime, SourceStatus, SourceTypeDefinition, VideoProvider } from '../core/definitions';
import type { Id, Project, SettingsBag, Source } from '../core/model';
import type { Unsubscribe } from '../core/services';
import type { SourceManager, SourceManagerDeps, SourceSnapshot } from './api';

interface Entry {
  kind: string;
  settings: Source['settings'];
  /** Version of `settings` as stored in the project, or as produced by migrate. */
  settingsVersion: number | undefined;
  runtimeState: SettingsBag | undefined;
  /** A migrated bag handed to the kernel for write-back; its echo through sync() is not an edit. */
  migratedPending: SettingsBag | null;
  rasterScale: number | null;
  runtime: SourceRuntime | null;
  /** Definition the runtime was created from, so unregistering or replacing it can be detected. */
  def: SourceTypeDefinition | null;
  unsubscribe: Unsubscribe | null;
  /** Reason there is no runtime: kind not registered, or create() threw. */
  failure: SourceStatus | null;
  tickReported: boolean;
}

export function createSourceManager(deps: SourceManagerDeps): SourceManager {
  const { registry, context } = deps;
  const entries = new Map<Id, Entry>();
  const listeners = new Set<(sourceId: Id) => void>();
  let active: ReadonlySet<Id> = new Set();
  let showing: ReadonlySet<Id> = new Set();
  let destroyed = false;
  let projectId: string;
  let creatorSlot:{id:string;runtime:BrowserRuntime;binding:CreatorBrowserBinding}|undefined;
  const retireCreator=()=>{const slot=creatorSlot;creatorSlot=undefined;slot?.binding.retire();slot?.runtime.destroy();};

  const log = (level: 'warn' | 'error', sourceId: Id, message: string, detail?: unknown) =>
    context.logger.log(level, `source:${sourceId}`, message, detail);

  const notify = (id: Id) => {
    for (const listener of [...listeners]) {
      try {
        listener(id);
      } catch (err) {
        log('error', id, 'source listener threw', err);
      }
    }
  };

  const destroyRuntime = (id: Id, entry: Entry) => {
    entry.unsubscribe?.();
    entry.unsubscribe = null;
    const runtime = entry.runtime;
    entry.runtime = null;
    try {
      runtime?.destroy();
    } catch (err) {
      log('error', id, 'destroy() threw', err);
    }
  };

  // The kernel answers these callbacks by mutating the project, which re-enters sync(); deferring
  // them keeps sync() and create() free of re-entrant document changes.
  const defer = (fn: () => void) =>
    queueMicrotask(() => {
      if (destroyed) return;
      try {
        fn();
      } catch (err) {
        context.logger.log('error', 'sources', 'source state write-back failed', err);
      }
    });

  const contextFor = (id: Id, entry: Entry): SourceContext => ({
    ...context,
    sourceId: id,
    projectId,
    saveRuntimeState(state) {
      if (destroyed || entries.get(id) !== entry) return;
      const copy = state === null ? null : structuredClone(state);
      entry.runtimeState = copy ?? undefined;
      defer(() => {
        if (entries.get(id) === entry) deps.onRuntimeState?.(id, copy);
      });
    },
  });

  /**
   * Settings to hand to the runtime: the stored bag, or its migration when the bag is older than
   * the kind's settingsVersion. A throwing migrate leaves the bag as stored; runtimes already
   * defend against bad values.
   */
  const prepareSettings = (id: Id, entry: Entry, def: SourceTypeDefinition): SettingsBag => {
    const target = def.settingsVersion;
    const from = entry.settingsVersion ?? 0;
    if (!target || !def.migrate || from >= target) return entry.settings;
    try {
      const migrated = def.migrate(structuredClone(entry.settings), from);
      entry.settings = migrated;
      entry.settingsVersion = target;
      entry.migratedPending = migrated;
      defer(() => {
        if (entries.get(id) === entry) deps.onSettingsMigrated?.(id, migrated, target);
      });
      return migrated;
    } catch (err) {
      log('error', id, `migrate() from settings version ${from} failed for "${entry.kind}"`, err);
      return entry.settings;
    }
  };

  const instantiate = (id: Id, entry: Entry) => {
    const def = registry.getSource(entry.kind);
    if (!def) {
      entry.failure = unsupported(entry.kind);
      return;
    }
    try {
      const settings = prepareSettings(id, entry, def);
      const runtime = def.create(contextFor(id, entry), settings, entry.runtimeState);
      entry.runtime = runtime;
      entry.def = def;
      entry.failure = null;
      entry.unsubscribe = runtime.subscribe(() => notify(id));
      runtime.setActive?.(active.has(id));
      runtime.setShowing?.(showing.has(id));
      if (entry.rasterScale !== null) runtime.setRasterScale?.(entry.rasterScale);
    } catch (err) {
      log('error', id, `create() failed for "${entry.kind}"`, err);
      entry.failure = {
        state: 'error',
        error: { code: 'unknown', message: err instanceof Error ? err.message : String(err) },
      };
    }
  };

  const unsupported = (kind: string): SourceStatus => ({
    state: 'error',
    error: { code: 'unsupported', message: `Source type "${kind}" is not available` },
  });

  // A kind that disappears (plugin unloaded) must not keep running the old code; one that is
  // registered again, or replaced by a different definition, is created afresh.
  const reconcileWithRegistry = () => {
    if (destroyed) return;
    for (const [id, entry] of [...entries]) {
      const current = registry.getSource(entry.kind);
      if (entry.runtime) {
        if (current === entry.def) continue;
        destroyRuntime(id, entry);
        entry.def = null;
        entry.failure = unsupported(entry.kind);
        if (current) instantiate(id, entry);
        notify(id);
      } else if (current && entry.failure?.error?.code === 'unsupported') {
        instantiate(id, entry);
        notify(id);
      }
    }
  };
  const unsubscribeRegistry = registry.subscribe(reconcileWithRegistry);

  const safe = <T>(id: Id, what: string, fn: () => T, fallback: T): T => {
    try {
      return fn();
    } catch (err) {
      log('error', id, `${what} threw`, err);
      return fallback;
    }
  };

  const manager: SourceManager = {
    mountCreatorBrowser(input,binding){
      if(destroyed||creatorSlot||input.projectId!==projectId||entries.has(input.sourceId))throw Error('creator_unavailable');
      const runtime=new BrowserRuntime({...context,sourceId:input.sourceId,projectId,saveRuntimeState(){throw Error('creator_private_source');}},input.settings,binding);
      const slot={id:input.sourceId,runtime,binding};creatorSlot=slot;
      return {binding,dispose(){if(creatorSlot===slot)retireCreator();}};
    },
    sync(project: Project) {
      if (destroyed) return;
      if(creatorSlot&&(projectId!==project.id||project.sources[creatorSlot.id]))retireCreator();
      if (projectId !== project.id) {
        for (const [id, entry] of entries) destroyRuntime(id, entry);
        entries.clear();
        projectId = project.id;
      }
      for (const [id, entry] of [...entries]) {
        if (project.sources[id]) continue;
        destroyRuntime(id, entry);
        entries.delete(id);
        notify(id);
      }
      for (const source of Object.values(project.sources)) {
        const existing = entries.get(source.id);
        if (existing && existing.kind !== source.kind) {
          destroyRuntime(source.id, existing);
          entries.delete(source.id);
        }
        let entry = entries.get(source.id);
        if (!entry) {
          entry = {
            kind: source.kind,
            settings: source.settings,
            settingsVersion: source.settingsVersion,
            runtimeState: source.runtimeState,
            migratedPending: null,
            rasterScale: null,
            runtime: null,
            def: null,
            unsubscribe: null,
            failure: null,
            tickReported: false,
          };
          entries.set(source.id, entry);
          instantiate(source.id, entry);
          notify(source.id);
          continue;
        }
        entry.runtimeState = source.runtimeState;
        if (entry.settings === source.settings) continue;
        const echo = entry.migratedPending;
        entry.migratedPending = null;
        if (echo && source.settingsVersion === entry.settingsVersion && JSON.stringify(source.settings) === JSON.stringify(echo)) {
          entry.settings = source.settings;
          continue;
        }
        entry.settings = source.settings;
        entry.settingsVersion = source.settingsVersion;
        entry.tickReported = false;
        const runtime = entry.runtime;
        if (!runtime) {
          // create() failed earlier (or the kind was missing): new settings are a fresh chance to start.
          instantiate(source.id, entry);
          notify(source.id);
          continue;
        }
        const settings = entry.def ? prepareSettings(source.id, entry, entry.def) : entry.settings;
        try {
          Promise.resolve(runtime.update(settings)).catch((err) => log('error', source.id, 'update() rejected', err));
        } catch (err) {
          log('error', source.id, 'update() threw', err);
        }
      }
    },

    get: (id) => creatorSlot?.id===id?creatorSlot.runtime:entries.get(id)?.runtime ?? undefined,

    getKind(id){return creatorSlot?.id===id?'browser':entries.get(id)?.kind;},
    getVideo(id): VideoProvider | null {
      const runtime = creatorSlot?.id===id?creatorSlot.runtime:entries.get(id)?.runtime;
      return runtime ? safe(id, 'getVideo()', () => runtime.getVideo(), null) : null;
    },

    getAudio(id): AudioNode | null {
      const runtime = creatorSlot?.id===id?creatorSlot.runtime:entries.get(id)?.runtime;
      return runtime ? safe(id, 'getAudio()', () => runtime.getAudio(), null) : null;
    },

    getSnapshot(id): SourceSnapshot | null {
      const entry = entries.get(id);
      if (!entry) return null;
      const runtime = entry.runtime;
      if (!runtime) return { status: entry.failure ?? { state: 'idle' }, hasVideo: false, hasAudio: false, width: 0, height: 0 };
      const video = safe(id, 'getVideo()', () => runtime.getVideo(), null);
      const snapshot: SourceSnapshot = {
        status: safe<SourceStatus>(id, 'getStatus()', () => runtime.getStatus(), { state: 'error', error: { code: 'unknown', message: 'getStatus() threw' } }),
        hasVideo: video !== null,
        hasAudio: safe(id, 'getAudio()', () => runtime.getAudio(), null) !== null,
        width: video?.width ?? 0,
        height: video?.height ?? 0,
      };
      if (runtime.getLiveInfo) {
        const live = safe<Record<string, string> | null>(id, 'getLiveInfo()', () => runtime.getLiveInfo?.() ?? null, null);
        if (live) snapshot.live = live;
      }
      if (runtime.getMediaState) {
        const media = safe<MediaState | null>(id, 'getMediaState()', () => runtime.getMediaState?.() ?? null, null);
        if (media) snapshot.media = media;
      }
      return snapshot;
    },

    setActive(programIds, showingIds) {
      active = new Set(programIds);
      showing = new Set([...showingIds, ...programIds]);
      for (const [id, entry] of entries) {
        const runtime = entry.runtime;
        if (runtime?.setActive) safe(id, 'setActive()', () => runtime.setActive?.(active.has(id)), undefined);
        if (runtime?.setShowing) safe(id, 'setShowing()', () => runtime.setShowing?.(showing.has(id)), undefined);
      }
    },

    setRasterScale(id, scale) {
      const entry = entries.get(id);
      if (!entry || !Number.isFinite(scale) || scale <= 0) return;
      entry.rasterScale = scale;
      const runtime = entry.runtime;
      if (runtime?.setRasterScale) safe(id, 'setRasterScale()', () => runtime.setRasterScale?.(scale), undefined);
    },

    tick(time: FrameTime) {
      if(creatorSlot){try{creatorSlot.runtime.tick();}catch{retireCreator();}}
      for (const [id, entry] of entries) {
        const runtime = entry.runtime;
        if (!runtime?.tick || !showing.has(id)) continue;
        try {
          runtime.tick(time);
        } catch (err) {
          // Reported once per settings revision so a broken source cannot flood the log at frame rate.
          if (!entry.tickReported) {
            entry.tickReported = true;
            log('error', id, 'tick() threw', err);
          }
        }
      }
    },

    subscribe(listener): Unsubscribe {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    destroy() {
      if (destroyed) return;
      destroyed = true;
      retireCreator();
      unsubscribeRegistry();
      for (const [id, entry] of entries) destroyRuntime(id, entry);
      entries.clear();
      listeners.clear();
    },
  };
  return manager;
}
